import { useCallback, useMemo, useState } from "react";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useForm } from "react-hook-form";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { useCachedDataLoader } from "../../../../common/hooks/use_cached_data_loader";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { zodResolver } from "@hookform/resolvers/zod";
import { ObraForm, ObraSchema } from "../schemas/Obras_informacoesgerais/obra";
import { useLocation } from "react-router";
import { Obra } from "../../domain/models/obra";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useObrasInformacoesGeraisFilter } from "../hooks/Obras_informacoesgerais/filter";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { ObraEstruturaOrganizacional } from "../../domain/models/obra_estrutura_organizacional";
import { useObrasInformacoesGeraisPagination } from "../hooks/Obras_informacoesgerais/pagination";
import { ObraEstruturaOrganizacionalForm } from "../schemas/Obras_informacoesgerais/obra_estrutura_organizacional";
import { AtividadeObra } from "../../domain/models/atividade_obra";
import { AtividadeObraForm } from "../schemas/Obras_informacoesgerais/atividade_obra";
import { EspecificidadeObra } from "../../domain/models/especificidade_obra";
import { EspecificidadeObraForm } from "../schemas/Obras_informacoesgerais/especificidade_obra";
import { IndicadorObra } from "../../domain/models/indicador_obra";
import { IndicadorObraForm } from "../schemas/Obras_informacoesgerais/indicador_obra";
import { ObraArquivo } from "../../domain/models/obra_arquivo";
import {
  initialObraArquivo,
  ObraArquivoForm,
  ObraArquivoSchema,
} from "../schemas/Obras_informacoesgerais/obra_arquivo";
import { ObraInformacoes } from "../../domain/models/obra_informacoes";
import { ObraInformacoesForm } from "../schemas/Obras_informacoesgerais/obra_informacoes";
import {
  useCreateDebounceHandler,
  useDebouncedCallback,
} from "../../../../common/hooks/use_debounced_callback";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { MapaConcretagemFiltro } from "../../domain/models/mapa_concretagem_filtro";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { useObrasCommonReports } from "../components/hooks/obras_common_reports";
import { useObrasTopHeaderState } from "../components/hooks/obras_top_header_state";
import { TelasIds } from "../../../../common/utils/tela_ids";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { FornecedorDropDown } from "../../../../common/domain/models/fornecedor_dropdown";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";
import { AtividadeObraFuncionario } from "../../domain/models/atividade_obra_funcionario";
import { IndicadorDropDown } from "../../domain/models/indicador_dropdown";
import { ObraEstruturaOrganizacionalFuncionario } from "../../domain/models/obra_estrutura_organizacional_funcionario";

import CommonConstants from "../../../../common/constants";

/**
 * LoadObraFuncState
 */

type LoadObraFuncState = {
  idObra?: number;
};

/**
 * LoadEstOrgFuncState
 */

type LoadEstOrgFuncState = {
  idObra?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * LoadAtividadesObraFuncState
 */

type LoadAtividadesObraFuncState = {
  idObra?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * LoadEspecificidadesObraFuncState
 */

type LoadEspecificidadesObraFuncState = {
  idObra?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * LoadEspecificidadesObraFuncState
 */

type LoadIndicadoresObraFuncState = {
  idObra?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * LoadObraArquivosFuncState
 */

type LoadObraArquivosFuncState = {
  idObra?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * LoadObraInformacoesFuncState
 */

type LoadObraInformacoesFuncState = {
  idObra?: number;
  data?: Date | null;
  informacoes?: string | null;
  page?: number;
  rowsPerPage?: number;
};

/**
 * isNovaObraPorEmpreendimento
 *
 * A forma de cadastro de uma nova obra vem da lista (?modo=empreendimento).
 */

export const MODO_CADASTRO_EMPREENDIMENTO = "empreendimento";

const isNovaObraPorEmpreendimento = () =>
  new URLSearchParams(window.location.search).get("modo") === MODO_CADASTRO_EMPREENDIMENTO;

/**
 * useObrasInformacoesGeraisViewModel
 */

export const useObrasInformacoesGeraisViewModel = () => {
  const {
    commons: {
      getCentroCustoDropDownUseCase,
      getEmpreendimentoDropDownUseCase,
      getFuncionariosDropDownUseCase,
      getFornecedorDropDownUseCase,
      getAllLogAlteracaoByParentUseCase,
      getAllLogAlteracaoUseCase,
    },
    obras: {
      getObraEstruturaOrganizacionalFuncionarioDropDownUseCase,
      getAtividadeObraResponsavelDropDownUseCase,
      getAtividadeObraCorresponsavelDropDownUseCase,
      getIndicadorDropDownUseCase,
      getObraByIdUseCase,
      deleteObraUseCase,
      createObraUseCase,
      updateObraUseCase,
      createObraEstruturaOrganizacionalUseCase,
      updateObraEstruturaOrganizacionalUseCase,
      deleteObraEstruturaOrganizacionalUseCase,
      getAllObraEstruturaOrganizacionalUseCase,
      createAtividadeObraUseCase,
      updateAtividadeObraUseCase,
      deleteAtividadeObraUseCase,
      getAllAtividadeObraUseCase,
      getAllEspecificidadeObraUseCase,
      createEspecificidadeObraUseCase,
      updateEspecificidadeObraUseCase,
      deleteEspecificidadeObraUseCase,
      getAllIndicadorObraUseCase,
      createIndicadorObraUseCase,
      updateIndicadorObraUseCase,
      deleteIndicadorObraUseCase,
      getAllObraArquivoUseCase,
      createObraArquivoUseCase,
      updateObraArquivoUseCase,
      deleteObraArquivoUseCase,
      getAllInformacaoObraUseCase,
      createObraInformacoesUseCase,
      updateObraInformacoesUseCase,
      deleteObraInformacoesUseCase,
      getMapaConcretagemFiltroUseCase,
      downloadObraDocumentoUseCase,
      visualizeObraDocumentoUseCase,
    },
  } = useDependencyInjection();

  const location = useLocation();

  const backOrNavigate = useBackOrNavigate();

  const debounceHandlerRef = useCreateDebounceHandler(750);

  const {
    pushInfoNotification,
    pushErrorNotification,
    setCustomLoading,
    setPageLoading,
    setPageSaving,
  } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [idObra, setIdObra] = useState<number>();

  const [centroCustosDropdown, setCentroCustosDropdown] =
    useState<CentroCustoDropDown[]>();
  const [empreendimentosDropdown, setEmpreendimentosDropdown] =
    useState<DropDown[]>();
  const [estOrgfuncionariosDropdown, setEstOrgfuncionariosDropdown] =
    useState<ObraEstruturaOrganizacionalFuncionario[]>();
  const [responsavelDropdown, setResponsavelDropdown] =
    useState<AtividadeObraFuncionario[]>();
  const [corresponsavelDropdown, setCorresponsavelDropdown] =
    useState<AtividadeObraFuncionario[]>();
  const [indicadoresDropdown, setIndicadoresDropdown] =
    useState<IndicadorDropDown[]>();
  const [funcionariosDropDown, setFuncionariosDropDown] =
    useState<FuncionarioDropDown[]>();
  const [fornecedoresDropDown, setFornecedoresDropDown] =
    useState<FornecedorDropDown[]>();

  const [estOrgLista, setEstOrgLista] =
    useState<ObraEstruturaOrganizacional[]>();
  const [atividadeObraLista, setAtividadeObraLista] =
    useState<AtividadeObra[]>();
  const [especificidadeObraLista, setEspecificidadeObraLista] =
    useState<EspecificidadeObra[]>();
  const [indicadorObraLista, setIndicadorObraLista] =
    useState<IndicadorObra[]>();
  const [obraArquivoLista, setObraArquivoLista] = useState<ObraArquivo[]>();
  const [obraInformacoesLista, setObraInformacoesLista] =
    useState<ObraInformacoes[]>();

  const [selectedObra, setSelectedObra] = useState<Obra>();
  const [selectedEstOrg, setSelectedEstOrg] =
    useState<ObraEstruturaOrganizacional>();
  const [selectedAtividadeObra, setSelectedAtividadeObra] =
    useState<AtividadeObra>();
  const [selectedEspecificidadeObra, setSelectedEspecificidadeObra] =
    useState<EspecificidadeObra>();
  const [selectedIndicadorObra, setSelectedIndicadorObra] =
    useState<IndicadorObra>();
  const [selectedObraArquivo, setSelectedObraArquivo] = useState<ObraArquivo>();
  const [selectedObraInformacoes, setSelectedObraInformacoes] =
    useState<ObraInformacoes>();

  const [obraDeleteModal, setObraDeleteModal] = useState(false);

  const [estOrgListaModal, setEstOrgListaModal] = useState(false);
  const [estOrgModal, setEstOrgModal] = useState(false);
  const [estOrgDeleteModal, setEstOrgDeleteModal] = useState(false);

  const [atividadeListaObraModal, setAtividadeListaObraModal] = useState(false);
  const [atividadeObraModal, setAtividadeObraModal] = useState(false);
  const [atividadeObraDeleteModal, setAtividadeObraDeleteModal] =
    useState(false);

  const [especificidadeListaObraModal, setEspecificidadeListaObraModal] =
    useState(false);
  const [especificidadeObraModal, setEspecificidadeObraModal] = useState(false);
  const [especificidadeObraDeleteModal, setEspecificidadeObraDeleteModal] =
    useState(false);

  const [indicadorObraListaModal, setIndicadorObraListaModal] = useState(false);
  const [indicadorObraModal, setIndicadorObraModal] = useState(false);
  const [indicadorObraDeleteModal, setIndicadorObraDeleteModal] =
    useState(false);

  const [obraArquivoListaModal, setObraArquivoListaModal] = useState(false);
  const [obraArquivoModal, setObraArquivoModal] = useState(false);
  const [obraArquivoDeleteModal, setObraArquivoDeleteModal] = useState(false);

  const [obraInformacoesListaModal, setObraInformacoesListaModal] =
    useState(false);
  const [obraInformacoesModal, setObraInformacoesModal] = useState(false);
  const [obraInformacoesDeleteModal, setObraInformacoesDeleteModal] =
    useState(false);

  const [estOrgFileUpload, setEstOrgFileUpload] = useState<File | null>(null);
  const [estOrgFileUploadUrl, setEstOrgFileUploadUrl] = useState<string | null>(
    null,
  );

  const [cantFileUpload, setCantFileUpload] = useState<File | null>(null);
  const [cantFileUploadUrl, setCantFileUploadUrl] = useState<string | null>(
    null,
  );

  const [filtroMapaConcretagem, setFiltroMapaConcretagem] =
    useState<MapaConcretagemFiltro>();

  const hookForm = useForm<ObraForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: { tipo: 0 },
    resolver: zodResolver(ObraSchema),
  });

  const hookFormObraArquivo = useForm<ObraArquivoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: initialObraArquivo,
    resolver: zodResolver(ObraArquivoSchema),
  });

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: setErrorMessage,
  });

  const obrasTopHeaderState = useObrasTopHeaderState();

  const [obrasReportState, initializeReportState] = useObrasCommonReports({
    idObra,
  });

  const filters = useObrasInformacoesGeraisFilter();
  const pagination = useObrasInformacoesGeraisPagination();

  const funcionariosInfObraDropDown = useMemo(() => {
    if (funcionariosDropDown === undefined) return null;
    return funcionariosDropDown.filter((x) => {
      if (selectedObraInformacoes) {
        if (x.idFuncionario === selectedObraInformacoes.idFuncionario) {
          return true;
        }
      }
      return x.canBeSelected;
    });
  }, [selectedObraInformacoes, funcionariosDropDown]);

  const funcionariosRevisadoDropDown = useMemo(() => {
    if (funcionariosDropDown === undefined) return null;
    return funcionariosDropDown.filter((x) => {
      if (selectedObra) {
        if (x.idFuncionario === selectedObra.idFuncionarioRevisado) {
          return true;
        }
      }
      return x.canBeSelected;
    });
  }, [selectedObra, funcionariosDropDown]);

  const funcionariosAprovadoDropDown = useMemo(() => {
    if (funcionariosDropDown === undefined) return null;
    return funcionariosDropDown.filter((x) => {
      if (selectedObra) {
        if (x.idFuncionario === selectedObra.idFuncionarioAprovado) {
          return true;
        }
      }
      return x.canBeSelected;
    });
  }, [selectedObra, funcionariosDropDown]);

  const filteredEstOrgfuncionariosDropdown = useMemo(() => {
    if (estOrgfuncionariosDropdown === undefined) return null;
    return estOrgfuncionariosDropdown.filter((x) => {
      if (selectedEstOrg) {
        if (selectedEstOrg.idFuncionario === x.idFuncionario) {
          return true;
        }
      }
      return x.canBeSelected;
    });
  }, [selectedEstOrg, estOrgfuncionariosDropdown]);

  /**
   * Funções de carregamento
   */

  // #region Funções de carregamento

  const loadCentroCustosDropdown = useCachedDataLoader({
    key: CommonConstants.cacheKey.centroCustoDropDown,
    loader: async () =>
      await getCentroCustoDropDownUseCase.getCentroCustoDropDown(),
    onSet: (dt) => setCentroCustosDropdown(dt),
    onError: (e) => {
      throw e;
    },
  });

  // Sem cache: a lista depende da obra em edição (empreendimentos cujo centro de custo já tem obra ficam de fora).
  const loadEmpreendimentosDropdown = useCallback(async (idObraEdicao?: number) => {
    const empreendimentos = await getEmpreendimentoDropDownUseCase.getEmpreendimentoDropDown(idObraEdicao ?? null);
    setEmpreendimentosDropdown(empreendimentos);
  }, [
    getEmpreendimentoDropDownUseCase,
  ]);

  const loadObraEstruturaOrganizacionalDropdown = useCachedDataLoader({
    key: CommonConstants.cacheKey.obraEstruturaOrganizacionalDropDown,
    loader: async () =>
      await getObraEstruturaOrganizacionalFuncionarioDropDownUseCase.getObraEstruturaOrganizacionalFuncionarioDropdown(),
    onSet: (dt) => setEstOrgfuncionariosDropdown(dt),
    onError: (e) => {
      throw e;
    },
  });

  const loadAtividadeObraResponsavelDropdown = useCachedDataLoader({
    key: CommonConstants.cacheKey.atividadeObraResponsavelDropDown,
    loader: async (id: number) =>
      await getAtividadeObraResponsavelDropDownUseCase.getAtividadeObraResponsavelDropdown(
        id,
      ),
    onSet: (dt) => setResponsavelDropdown(dt),
    onError: (e) => {
      throw e;
    },
  });

  const loadAtividadeObraCorresponsavelDropdown = useCachedDataLoader({
    key: CommonConstants.cacheKey.atividadeObraCorresponsavelDropDown,
    loader: async (id: number) =>
      await getAtividadeObraCorresponsavelDropDownUseCase.getAtividadeObraCorresponsavelDropdown(
        id,
      ),
    onSet: (dt) => setCorresponsavelDropdown(dt),
    onError: (e) => {
      throw e;
    },
  });

  const loadIndicadoresDropdown = useCachedDataLoader({
    key: CommonConstants.cacheKey.indicadoresDropDown,
    loader: async (id: number) =>
      await getIndicadorDropDownUseCase.getIndicadorDropdown(id),
    onSet: (dt) => setIndicadoresDropdown(dt),
    onError: (e) => {
      throw e;
    },
  });

  const loadFuncionariosDropdown = useCachedDataLoader({
    key: CommonConstants.cacheKey.funcionariosDropDown,
    loader: async () =>
      await getFuncionariosDropDownUseCase.getFuncionariosDropDown(
        undefined,
        true,
      ),
    onSet: (dt) => setFuncionariosDropDown(dt),
    onError: (e) => {
      throw e;
    },
  });

  const loadFornecedoresDropdown = useCachedDataLoader({
    key: CommonConstants.cacheKey.fornecedoresDropDown,
    loader: async () =>
      await getFornecedorDropDownUseCase.getFornecedorDropDown(),
    onSet: (dt) => setFornecedoresDropDown(dt),
    onError: (e) => {
      throw e;
    },
  });

  const loadEstOrg = useCallback(
    async (fs?: LoadEstOrgFuncState) => {
      try {
        const filteredIdObra = fs?.idObra ?? idObra;

        const filteredPage = fs?.page ?? pagination.estOrg.page;
        const filteredRowsPerPage =
          fs?.rowsPerPage ?? pagination.estOrg.rowsPerPage;

        await getAllObraEstruturaOrganizacionalUseCase.getAllObraEstruturaOrganizacional(
          filteredIdObra,
          filteredPage,
          filteredRowsPerPage,
        );

        setEstOrgLista(getAllObraEstruturaOrganizacionalUseCase.items);

        pagination.estOrgSetter.setTotalItems(
          getAllObraEstruturaOrganizacionalUseCase.totalItems,
        );
      } catch (e: unknown) {
        throw e;
      }
    },
    [pagination, idObra, getAllObraEstruturaOrganizacionalUseCase],
  );

  const loadAtividadesObra = useCallback(
    async (fs?: LoadAtividadesObraFuncState) => {
      try {
        const filteredIdObra = fs?.idObra ?? idObra;

        const filteredPage = fs?.page ?? pagination.atividadesObra.page;
        const filteredRowsPerPage =
          fs?.rowsPerPage ?? pagination.atividadesObra.rowsPerPage;

        await getAllAtividadeObraUseCase.getAllAtividadeObra(
          filteredIdObra,
          filteredPage,
          filteredRowsPerPage,
        );

        setAtividadeObraLista(getAllAtividadeObraUseCase.items);

        pagination.atividadesObraSetter.setTotalItems(
          getAllAtividadeObraUseCase.totalItems,
        );
      } catch (e: unknown) {
        throw e;
      }
    },
    [pagination, idObra, getAllAtividadeObraUseCase],
  );

  const loadEspecificidadesObra = useCallback(
    async (fs?: LoadEspecificidadesObraFuncState) => {
      try {
        const filteredIdObra = fs?.idObra ?? idObra;

        const filteredPage = fs?.page ?? pagination.especificidadesObra.page;
        const filteredRowsPerPage =
          fs?.rowsPerPage ?? pagination.especificidadesObra.rowsPerPage;

        await getAllEspecificidadeObraUseCase.getAllEspecificidadeObra(
          filteredIdObra,
          filteredPage,
          filteredRowsPerPage,
        );

        setEspecificidadeObraLista(getAllEspecificidadeObraUseCase.items);

        pagination.especificidadesObraSetter.setTotalItems(
          getAllEspecificidadeObraUseCase.totalItems,
        );
      } catch (e: unknown) {
        throw e;
      }
    },
    [pagination, idObra, getAllEspecificidadeObraUseCase],
  );

  const loadIndicadoresObra = useCallback(
    async (fs?: LoadIndicadoresObraFuncState) => {
      try {
        const filteredIdObra = fs?.idObra ?? idObra;

        const filteredPage = fs?.page ?? pagination.indicadoresObra.page;
        const filteredRowsPerPage =
          fs?.rowsPerPage ?? pagination.indicadoresObra.rowsPerPage;

        await getAllIndicadorObraUseCase.getAllIndicadorObra(
          filteredIdObra,
          filteredPage,
          filteredRowsPerPage,
        );

        setIndicadorObraLista(getAllIndicadorObraUseCase.items);

        pagination.indicadoresObraSetter.setTotalItems(
          getAllIndicadorObraUseCase.totalItems,
        );
      } catch (e: unknown) {
        setIndicadorObraLista(undefined);
        throw e;
      }
    },
    [pagination, idObra, getAllIndicadorObraUseCase],
  );

  const loadObraArquivos = useCallback(
    async (fs?: LoadObraArquivosFuncState) => {
      try {
        const filteredIdObra = fs?.idObra ?? idObra;

        const filteredPage = fs?.page ?? pagination.obraArquivos.page;
        const filteredRowsPerPage =
          fs?.rowsPerPage ?? pagination.obraArquivos.rowsPerPage;

        await getAllObraArquivoUseCase.getAllObraArquivo(
          filteredIdObra,
          null,
          null,
          null,
          null,
          filteredPage,
          filteredRowsPerPage,
        );

        setObraArquivoLista(getAllObraArquivoUseCase.items);

        pagination.obraArquivosSetter.setTotalItems(
          getAllObraArquivoUseCase.totalItems,
        );
      } catch (e: unknown) {
        setObraArquivoLista(undefined);
        throw e;
      }
    },
    [pagination, idObra, getAllObraArquivoUseCase],
  );

  const loadObraInformacoes = useCallback(
    async (fs?: LoadObraInformacoesFuncState) => {
      try {
        const filteredIdObra = fs?.idObra ?? idObra;
        const filteredData = evaluateParamValue(fs?.data, filters.data);
        const filteredInformacoes = evaluateParamValue(
          fs?.informacoes,
          filters.informacoes,
        );

        const filteredPage = fs?.page ?? pagination.obraInformacoes.page;
        const filteredRowsPerPage =
          fs?.rowsPerPage ?? pagination.obraInformacoes.rowsPerPage;

        await getAllInformacaoObraUseCase.getAllObraInformacoes(
          filteredIdObra,
          filteredData,
          filteredInformacoes,
          filteredPage,
          filteredRowsPerPage,
        );

        setObraInformacoesLista(getAllInformacaoObraUseCase.items);

        pagination.obraInformacoesSetter.setTotalItems(
          getAllInformacaoObraUseCase.totalItems,
        );
      } catch (e: unknown) {
        setObraInformacoesLista(undefined);
        throw e;
      }
    },
    [filters, pagination, idObra, getAllInformacaoObraUseCase],
  );

  const loadMapaConcretagemFiltro = useCallback(async () => {
    try {
      const filtros =
        await getMapaConcretagemFiltroUseCase.getMapaConcretagemFiltro();
      setFiltroMapaConcretagem(filtros ?? undefined);
    } catch (e: unknown) {
      setFiltroMapaConcretagem(undefined);
      throw e;
    }
  }, [getMapaConcretagemFiltroUseCase]);

  const loadObra = useCallback(
    async (fs?: LoadObraFuncState) => {
      try {
        const filteredId = fs?.idObra ?? idObra;
        if (filteredId) {
          const obra = await getObraByIdUseCase.getObraById(
            TelasIds.Obras_informacoesgerais,
            filteredId,
          );

          setSelectedObra(obra);

          if (obra) {
            hookForm.setValue("tipo", obra.status ?? 0);
            hookForm.setValue(
              "idFuncionarioRevisado",
              obra.idFuncionarioRevisado,
            );
            hookForm.setValue(
              "idFuncionarioAprovado",
              obra.idFuncionarioAprovado,
            );
            hookForm.setValue("historicoRevisoes", obra.historico);
            hookForm.setValue("apresentacaoObra", obra.apresentacaoObra);
            hookForm.setValue("meios", obra.assegurarAmbiente);
            hookForm.setValue("pctPdeFadDatec", obra.pctPdeFad);
            hookForm.setValue("processo", obra.observacoes);

            hookForm.setValue("porEmpreendimento", obra.idEmpreendimento != null);
            hookForm.setValue("idEmpreendimento", obra.idEmpreendimento ?? null);

            if (obra.idCentroCusto) {
              hookForm.setValue("idCentroCusto", obra.idCentroCusto);
            } else {
              hookForm.resetField("idCentroCusto");
            }

          } else {
            hookForm.reset({ porEmpreendimento: isNovaObraPorEmpreendimento() });
          }
        } else {
          hookForm.reset({ tipo: 0, porEmpreendimento: isNovaObraPorEmpreendimento() });
        }
      } catch (e: unknown) {
        setSelectedObra(undefined);
        throw e;
      }
    },
    [idObra, hookForm, getObraByIdUseCase],
  );

  // #endregion Funções de carregamento

  /**
   * useEffect
   */

  // #region useEffect

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get("id") ?? null;
      const locationIdObra = locationId ? Number(locationId) : undefined;
      const jobs = [];

      setIdObra(locationIdObra);

      await loadObra({ idObra: locationIdObra });

      jobs.push(loadCentroCustosDropdown());
      jobs.push(loadEmpreendimentosDropdown(locationIdObra));
      jobs.push(loadObraEstruturaOrganizacionalDropdown());
      jobs.push(loadFuncionariosDropdown());
      jobs.push(loadFornecedoresDropdown());
      jobs.push(loadMapaConcretagemFiltro());

      if (locationIdObra) {
        jobs.push(initializeReportState(locationIdObra));
        jobs.push(loadAtividadeObraResponsavelDropdown(locationIdObra));
        jobs.push(loadAtividadeObraCorresponsavelDropdown(locationIdObra));
        jobs.push(loadIndicadoresDropdown(locationIdObra));
        jobs.push(loadEstOrg({ idObra: locationIdObra }));
        jobs.push(loadAtividadesObra({ idObra: locationIdObra }));
        jobs.push(loadEspecificidadesObra({ idObra: locationIdObra }));
        jobs.push(loadIndicadoresObra({ idObra: locationIdObra }));
        jobs.push(loadObraArquivos({ idObra: locationIdObra }));
        jobs.push(loadObraInformacoes({ idObra: locationIdObra }));
      }
      jobs.push(loadMapaConcretagemFiltro());
      jobs.push(loadFuncionariosDropdown());
      jobs.push(loadFornecedoresDropdown());
      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da pagina!")[0]);
    }
  });

  // #endregion useEffect

  /**
   * Eventos
   */

  // #region Eventos

  const handleSubmit = useCallback(
    async (form: ObraForm) => {
      try {
        setSaving(true);
        setPageSaving(true);
        if (selectedObra) {
          await updateObraUseCase.updateObra(
            selectedObra.idObra,
            form.tipo,
            form.idFuncionarioAprovado,
            form.idFuncionarioRevisado,
            form.porEmpreendimento ? null : form.idCentroCusto,
            form.porEmpreendimento ? form.idEmpreendimento : null,
            form.historicoRevisoes,
            form.apresentacaoObra,
            form.processo,
            form.pctPdeFadDatec,
            form.meios,
            selectedObra.imgEo,
            selectedObra.imgCo,
            estOrgFileUpload,
            cantFileUpload,
          );

          await loadObra();
        } else {
          const createdIdObra = await createObraUseCase.createObra(
            form.tipo,
            form.idFuncionarioAprovado,
            form.idFuncionarioRevisado,
            form.porEmpreendimento ? null : form.idCentroCusto,
            form.porEmpreendimento ? form.idEmpreendimento : null,
            form.historicoRevisoes,
            form.apresentacaoObra,
            form.processo,
            form.pctPdeFadDatec,
            form.meios,
            estOrgFileUpload,
            cantFileUpload,
          );

          setIdObra(createdIdObra);

          await loadObra({ idObra: createdIdObra });

          const params = new URLSearchParams(window.location.search);
          params.set("id", createdIdObra.toString());

          const newUrl = `${window.location.pathname}?${params.toString()}`;
          window.history.pushState({}, "", newUrl);
        }

        pushInfoNotification("Sucesso", "Registro salvo com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao salvar obra!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    },
    [
      selectedObra,
      estOrgFileUpload,
      cantFileUpload,
      createObraUseCase,
      updateObraUseCase,
      setPageSaving,
      loadObra,
      pushInfoNotification,
      pushErrorNotification,
    ],
  );

  const handleDeleteObraModalOpenState = useCallback((openState: boolean) => {
    setObraDeleteModal(openState);
  }, []);

  const handleDeleteConfirmObra = useCallback(async () => {
    if (idObra) {
      try {
        setSaving(true);
        await deleteObraUseCase.deleteObra(
          TelasIds.Obras_informacoesgerais,
          idObra,
        );
        pushInfoNotification("Informação", "Obra excluida com sucesso!");
        backOrNavigate(-1);
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao excluir obra!"),
        );
      } finally {
        setSaving(false);
        setObraDeleteModal(false);
      }
    }
  }, [
    idObra,
    deleteObraUseCase,
    pushErrorNotification,
    pushInfoNotification,
    backOrNavigate,
  ]);

  const handlePopperOptionSelect = useCallback(
    async (idOption: number) => {
      switch (idOption) {
        case 1 /* Novo */:
          setPageLoading(true);

          setTimeout(() => {
            setIdObra(undefined);
            setSelectedObra(undefined);
            setErrorMessage(undefined);
            setEstOrgLista(undefined);
            setAtividadeObraLista(undefined);
            setEspecificidadeObraLista(undefined);
            setIndicadorObraLista(undefined);
            setObraArquivoLista(undefined);
            setObraInformacoesLista(undefined);
            setSelectedObra(undefined);
            setSelectedEstOrg(undefined);
            setSelectedAtividadeObra(undefined);
            setSelectedEspecificidadeObra(undefined);
            setSelectedIndicadorObra(undefined);
            setSelectedObraArquivo(undefined);
            setSelectedObraInformacoes(undefined);
            setObraDeleteModal(false);
            setEstOrgModal(false);
            setEstOrgDeleteModal(false);
            setAtividadeObraModal(false);
            setAtividadeObraDeleteModal(false);
            setEspecificidadeObraModal(false);
            setEspecificidadeObraDeleteModal(false);
            setIndicadorObraModal(false);
            setIndicadorObraDeleteModal(false);
            setObraArquivoModal(false);
            setObraArquivoDeleteModal(false);
            setObraInformacoesModal(false);
            setObraInformacoesDeleteModal(false);
            setEstOrgFileUpload(null);
            setEstOrgFileUploadUrl(null);
            setCantFileUpload(null);
            setCantFileUploadUrl(null);

            hookForm.reset({ porEmpreendimento: isNovaObraPorEmpreendimento() });

            loadEmpreendimentosDropdown().catch((e: unknown) =>
              pushErrorNotification("Erro", processError(e, "Falha ao buscar empreendimentos!")));

            setTimeout(() => setPageLoading(false), 500);
          }, 0);

          break;

        case 2 /* Excluir */:
          if (idObra) {
            setObraDeleteModal(true);
          }
          break;
      }
    },
    [idObra, hookForm, setPageLoading, loadEmpreendimentosDropdown, pushErrorNotification],
  );

  const handleReload = useCallback(async () => {
    try {
      const jobs = [];

      setLoading(true);

      await loadObra();

      jobs.push(loadCentroCustosDropdown());
      jobs.push(loadEmpreendimentosDropdown(idObra));
      jobs.push(loadObraEstruturaOrganizacionalDropdown());

      if (idObra) {
        jobs.push(initializeReportState(idObra));
        jobs.push(loadAtividadeObraResponsavelDropdown(idObra));
        jobs.push(loadAtividadeObraCorresponsavelDropdown(idObra));
        jobs.push(loadIndicadoresDropdown(idObra));
        jobs.push(loadFuncionariosDropdown());
        jobs.push(loadFornecedoresDropdown());
        jobs.push(loadMapaConcretagemFiltro());
        jobs.push(loadEstOrg());
        jobs.push(loadAtividadesObra());
        jobs.push(loadEspecificidadesObra());
        jobs.push(loadIndicadoresObra());
        jobs.push(loadObraArquivos());
        jobs.push(loadObraInformacoes());
      }
      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao buscar dados da pagina!"),
      );
    } finally {
      setLoading(false);
    }
  }, [
    idObra,
    initializeReportState,
    loadCentroCustosDropdown,
    loadObraEstruturaOrganizacionalDropdown,
    loadAtividadeObraResponsavelDropdown,
    loadAtividadeObraCorresponsavelDropdown,
    loadFuncionariosDropdown,
    loadIndicadoresDropdown,
    loadFornecedoresDropdown,
    loadMapaConcretagemFiltro,
    loadObra,
    loadEstOrg,
    loadAtividadesObra,
    loadEspecificidadesObra,
    loadIndicadoresObra,
    loadObraArquivos,
    loadObraInformacoes,
    pushErrorNotification,
  ]);

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: "/Obras_lista" });
  }, [backOrNavigate]);

  const handleStartEstOrgFileUpload = useCallback(
    (file: File | null) => {
      if (estOrgFileUploadUrl) {
        URL.revokeObjectURL(estOrgFileUploadUrl);
      }
      setEstOrgFileUpload(file);
      setEstOrgFileUploadUrl(file ? URL.createObjectURL(file) : null);
    },
    [estOrgFileUploadUrl],
  );

  const handleStartCantFileUpload = useCallback(
    (file: File | null) => {
      if (cantFileUploadUrl) {
        URL.revokeObjectURL(cantFileUploadUrl);
      }
      setCantFileUpload(file);
      setCantFileUploadUrl(file ? URL.createObjectURL(file) : null);
    },
    [cantFileUploadUrl],
  );

  const handleDeleteEstOrgFileUpload = useCallback(() => {
    try {
      const imageInput = document.getElementById("estOrgImageInput");
      if (imageInput instanceof HTMLInputElement) {
        imageInput.value = "";
      }
    } catch (_: unknown) { }
    if (selectedObra && selectedObra.imgEo) {
      setSelectedObra({ ...selectedObra, imgEo: null });
    }
    if (estOrgFileUploadUrl) {
      URL.revokeObjectURL(estOrgFileUploadUrl);
    }
    setEstOrgFileUpload(null);
    setEstOrgFileUploadUrl(null);
  }, [selectedObra, estOrgFileUploadUrl]);

  const handleDeleteCantFileUpload = useCallback(() => {
    try {
      const imageInput = document.getElementById("cantImageInput");
      if (imageInput instanceof HTMLInputElement) {
        imageInput.value = "";
      }
    } catch (_: unknown) { }
    if (selectedObra && selectedObra.imgCo) {
      setSelectedObra({ ...selectedObra, imgCo: null });
    }
    if (cantFileUploadUrl) {
      URL.revokeObjectURL(cantFileUploadUrl);
    }
    setCantFileUpload(null);
    setCantFileUploadUrl(null);
    document.getElementById("estOrgImageInput");
  }, [selectedObra, cantFileUploadUrl]);

  const handleViewObraArquivo = useCallback(
    async (idObraArquivo: number, nomeArquivo: string) => {
      try {
        setCustomLoading(true);
        await visualizeObraDocumentoUseCase.visualizeObraDocumento(
          idObraArquivo,
          nomeArquivo,
        );
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao deletar baixar documento!"),
        );
      } finally {
        setCustomLoading(false);
      }
    },
    [visualizeObraDocumentoUseCase, pushErrorNotification, setCustomLoading],
  );

  const handleDownloadObraArquivo = useCallback(
    async (idObraArquivo: number, nomeArquivo: string) => {
      try {
        setLoading(true);
        await downloadObraDocumentoUseCase.downloadObraDocumento(
          idObraArquivo,
          nomeArquivo,
        );
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao deletar baixar documento!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [downloadObraDocumentoUseCase, pushErrorNotification],
  );

  // #endregion Eventos

  /**
   * Estrutura Organizacional
   */

  // #region Estrutura Organizacional

  const handleEstOrgListaOpenState = useCallback((openState: boolean) => {
    setEstOrgListaModal(openState);
  }, []);

  const handleCadastroEstOrgOpenState = useCallback(
    (idEstruturaOrganizacional: number | null, openState: boolean) => {
      if (openState) {
        if (idEstruturaOrganizacional && estOrgLista) {
          setSelectedEstOrg(
            estOrgLista.find(
              (x) => x.idEstruturaOrganizacional === idEstruturaOrganizacional,
            ),
          );
        } else {
          setSelectedEstOrg(undefined);
        }
      }
      setEstOrgModal(openState);
    },
    [estOrgLista],
  );

  const handleEstOrgSubmit = useCallback(
    async (form: ObraEstruturaOrganizacionalForm) => {
      if (selectedObra) {
        try {
          setSaving(true);

          if (selectedEstOrg) {
            await updateObraEstruturaOrganizacionalUseCase.updateObraEstruturaOrganizacional(
              selectedEstOrg.idEstruturaOrganizacional,
              form.idFuncionario,
              form.idCargo,
            );
          } else {
            await createObraEstruturaOrganizacionalUseCase.createObraEstruturaOrganizacional(
              selectedObra.idObra,
              form.idFuncionario,
              form.idCargo,
            );
          }

          await loadEstOrg();
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao criar estrutura organizacional!"),
          );
        } finally {
          setSaving(false);
          setEstOrgModal(false);
        }
      }
    },
    [
      selectedObra,
      selectedEstOrg,
      createObraEstruturaOrganizacionalUseCase,
      updateObraEstruturaOrganizacionalUseCase,
      pushErrorNotification,
      loadEstOrg,
    ],
  );

  const handleDeleteEstOrgModalOpenState = useCallback(
    (idEstruturaOrganizacional: number | null, openState: boolean) => {
      if (!saving && !loading) {
        if (openState) {
          if (idEstruturaOrganizacional && estOrgLista) {
            setSelectedEstOrg(
              estOrgLista.find(
                (x) =>
                  x.idEstruturaOrganizacional === idEstruturaOrganizacional,
              ),
            );
          } else {
            setSelectedEstOrg(undefined);
          }
        }
        setEstOrgDeleteModal(openState);
      }
    },
    [saving, loading, estOrgLista],
  );

  const handleDeleteConfirmEstOrg = useCallback(async () => {
    if (selectedEstOrg) {
      try {
        setSaving(true);
        await deleteObraEstruturaOrganizacionalUseCase.deleteObraEstruturaOrganizacional(
          selectedEstOrg.idEstruturaOrganizacional,
        );
        await loadEstOrg();
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao excluir estrutura organizacional!"),
        );
      } finally {
        setSaving(false);
        setEstOrgDeleteModal(false);
      }
    }
  }, [
    selectedEstOrg,
    deleteObraEstruturaOrganizacionalUseCase,
    pushErrorNotification,
    loadEstOrg,
  ]);

  const handleEstOrgPageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useObrasInformacoesGeraisPagination
          .getState()
          .estOrgSetter.setPage(page + 1);
        await loadEstOrg({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [pushErrorNotification, loadEstOrg],
  );

  const handleEstOrgRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        useObrasInformacoesGeraisPagination.getState().estOrgSetter.setPage(1);
        useObrasInformacoesGeraisPagination
          .getState()
          .estOrgSetter.setRowsPerPage(rowsPerPage);
        await loadEstOrg({ page: 1, rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [pushErrorNotification, loadEstOrg],
  );

  // #endregion Estrutura Organizacional

  /**
   * Atividades
   */

  // #region Atividades

  const handleAtividadeListaModalOpenState = useCallback(
    (openState: boolean) => {
      setAtividadeListaObraModal(openState);
    },
    [],
  );

  const handleCadastroAtividadeOpenState = useCallback(
    async (idAtividadeObra: number | null, openState: boolean) => {
      if (openState) {
        if (idAtividadeObra && atividadeObraLista) {
          const atividade = atividadeObraLista.find(
            (x) => x.idAtividadeObra === idAtividadeObra,
          );
          if (atividade) {
            if (atividade.idFuncionarioResp) {
              await loadAtividadeObraResponsavelDropdown(
                atividade.idFuncionarioResp,
              );
            }
            if (atividade.idFuncionarioCoresp) {
              await loadAtividadeObraCorresponsavelDropdown(
                atividade.idFuncionarioCoresp,
              );
            }
          }
          setSelectedAtividadeObra(atividade);
        } else {
          setSelectedAtividadeObra(undefined);
        }
      }
      setAtividadeObraModal(openState);
    },
    [
      atividadeObraLista,
      loadAtividadeObraResponsavelDropdown,
      loadAtividadeObraCorresponsavelDropdown,
    ],
  );

  const handleAtividadeSubmit = useCallback(
    async (form: AtividadeObraForm) => {
      if (selectedObra) {
        try {
          setSaving(true);
          if (selectedAtividadeObra) {
            await updateAtividadeObraUseCase.updateAtividadeObra(
              selectedAtividadeObra.idAtividadeObra,
              form.atividade,
              form.idFuncionarioResponsavel,
              form.funcionarioResponsavel,
              form.preencherResponsavel,
              form.idFuncionarioCorresponsavel,
              form.funcionarioCorresponsavel,
              form.preencherCorresponsavel,
            );
          } else {
            await createAtividadeObraUseCase.createAtividadeObra(
              selectedObra.idObra,
              form.atividade,
              form.idFuncionarioResponsavel,
              form.funcionarioResponsavel,
              form.preencherResponsavel,
              form.idFuncionarioCorresponsavel,
              form.funcionarioCorresponsavel,
              form.preencherCorresponsavel,
            );
          }
          await loadAtividadesObra();
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao criar atividade de obra!"),
          );
        } finally {
          setSaving(false);
          setAtividadeObraModal(false);
        }
      }
    },
    [
      selectedObra,
      selectedAtividadeObra,
      createAtividadeObraUseCase,
      updateAtividadeObraUseCase,
      loadAtividadesObra,
      pushErrorNotification,
    ],
  );

  const handleDeleteAtividadeModalOpenState = useCallback(
    (idAtividadeObra: number | null, openState: boolean) => {
      if (openState) {
        if (idAtividadeObra && atividadeObraLista) {
          setSelectedAtividadeObra(
            atividadeObraLista.find(
              (x) => x.idAtividadeObra === idAtividadeObra,
            ),
          );
        } else {
          setSelectedAtividadeObra(undefined);
        }
      }
      setAtividadeObraDeleteModal(openState);
    },
    [atividadeObraLista],
  );

  const handleDeleteConfirmAtividade = useCallback(async () => {
    if (selectedAtividadeObra) {
      try {
        setSaving(true);
        await deleteAtividadeObraUseCase.deleteAtividadeObra(
          selectedAtividadeObra.idAtividadeObra,
        );
        await loadAtividadesObra();
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao excluir estrutura organizacional!"),
        );
      } finally {
        setSaving(false);
        setSelectedAtividadeObra(undefined);
        setAtividadeObraDeleteModal(false);
      }
    }
  }, [
    selectedAtividadeObra,
    deleteAtividadeObraUseCase,
    loadAtividadesObra,
    pushErrorNotification,
  ]);

  const handleAtividadesPageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useObrasInformacoesGeraisPagination
          .getState()
          .atividadesObraSetter.setPage(page + 1);
        await loadAtividadesObra({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [pushErrorNotification, loadAtividadesObra],
  );

  const handleAtividadesRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        useObrasInformacoesGeraisPagination
          .getState()
          .atividadesObraSetter.setPage(1);
        useObrasInformacoesGeraisPagination
          .getState()
          .atividadesObraSetter.setRowsPerPage(rowsPerPage);
        await loadAtividadesObra({ page: 1, rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [pushErrorNotification, loadAtividadesObra],
  );

  // #endregion Atividades

  /**
   * Particularidades da obra
   */

  // #region Particularidades da obra

  const handleEspecificidadeListaObraModalOpenState = useCallback(
    (openState: boolean) => {
      setEspecificidadeListaObraModal(openState);
    },
    [],
  );

  const handleCadastroEspecificidadeObraOpenState = useCallback(
    (idEspecificidadeObra: number | null, openState: boolean) => {
      if (openState) {
        if (idEspecificidadeObra && especificidadeObraLista) {
          setSelectedEspecificidadeObra(
            especificidadeObraLista.find(
              (x) => x.idEspecificidadeObra === idEspecificidadeObra,
            ),
          );
        } else {
          setSelectedEspecificidadeObra(undefined);
        }
      }
      setEspecificidadeObraModal(openState);
    },
    [especificidadeObraLista],
  );

  const handleEspecificidadeObraSubmit = useCallback(
    async (form: EspecificidadeObraForm) => {
      if (selectedObra) {
        try {
          setSaving(true);
          if (selectedEspecificidadeObra) {
            await updateEspecificidadeObraUseCase.updateEspecificidadeObra(
              selectedEspecificidadeObra.idEspecificidadeObra,
              form.item,
              form.formaControle,
            );
          } else {
            await createEspecificidadeObraUseCase.createEspecificidadeObra(
              selectedObra.idObra,
              form.item,
              form.formaControle,
            );
          }
          await loadEspecificidadesObra();
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao excluir particularidade de obra!"),
          );
        } finally {
          setSaving(false);
          setEspecificidadeObraModal(false);
        }
      }
    },
    [
      selectedObra,
      selectedEspecificidadeObra,
      createEspecificidadeObraUseCase,
      updateEspecificidadeObraUseCase,
      pushErrorNotification,
      loadEspecificidadesObra,
    ],
  );

  const handleDeleteEspecificidadeObraModalOpenState = useCallback(
    (idEspecificidadeObra: number | null, openState: boolean) => {
      if (openState) {
        if (idEspecificidadeObra && especificidadeObraLista) {
          setSelectedEspecificidadeObra(
            especificidadeObraLista.find(
              (x) => x.idEspecificidadeObra === idEspecificidadeObra,
            ),
          );
        } else {
          setSelectedEspecificidadeObra(undefined);
        }
      }
      setEspecificidadeObraDeleteModal(openState);
    },
    [especificidadeObraLista],
  );

  const handleDeleteConfirmEspecificidadeObra = useCallback(async () => {
    try {
      setSaving(true);
      if (selectedEspecificidadeObra) {
        await deleteEspecificidadeObraUseCase.deleteEspecificidadeObra(
          selectedEspecificidadeObra.idEspecificidadeObra,
        );
        await loadEspecificidadesObra();
      }
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao excluir particularidade de obra!"),
      );
    } finally {
      setSaving(false);
      setEspecificidadeObraDeleteModal(false);
    }
  }, [
    selectedEspecificidadeObra,
    deleteEspecificidadeObraUseCase,
    pushErrorNotification,
    loadEspecificidadesObra,
  ]);

  const handleEspecificidadesObraPageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useObrasInformacoesGeraisPagination
          .getState()
          .especificidadesObraSetter.setPage(page + 1);
        await loadEspecificidadesObra({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [loadEspecificidadesObra, pushErrorNotification],
  );

  const handleEspecificidadesObraRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        useObrasInformacoesGeraisPagination
          .getState()
          .especificidadesObraSetter.setPage(1);
        useObrasInformacoesGeraisPagination
          .getState()
          .especificidadesObraSetter.setRowsPerPage(rowsPerPage);
        await loadEspecificidadesObra({ page: 1, rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados da pagina!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [loadEspecificidadesObra, pushErrorNotification],
  );

  // #endregion Particularidades da obra

  /**
   * Objetivos Específicos da Obra
   */

  // #region Objetivos Específicos da Obra

  const handleIndicadorObraListaModalOpenState = useCallback(
    async (openState: boolean) => {
      if (idObra) {
        try {
          setLoading(true);
          setIndicadorObraListaModal(openState);
          await loadIndicadoresDropdown(idObra);
          await loadIndicadoresObra();
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao buscar indicadores!"),
          );
        } finally {
          setLoading(false);
        }
      }
    },
    [
      idObra,
      loadIndicadoresDropdown,
      loadIndicadoresObra,
      pushErrorNotification,
    ],
  );

  const handleCadastroIndicadorObraOpenState = useCallback((idObraIndicador: number | null, openState: boolean) => {
    if (openState) {
      if (idObraIndicador && indicadorObraLista) {
        setSelectedIndicadorObra(indicadorObraLista.find(x => x.idObraIndicador === idObraIndicador));
      } else {
        setSelectedIndicadorObra(undefined);
      }
    }
    setIndicadorObraModal(openState);
  }, [
    indicadorObraLista,
  ]);

  const handleIndicadorObraSubmit = useCallback(async (form: IndicadorObraForm) => {
    if (selectedObra) {
      try {
        setSaving(true);

        if (selectedIndicadorObra) {
          await updateIndicadorObraUseCase.updateIndicadorObra(
            selectedIndicadorObra.idObraIndicador,
            form.resultado,
          );
        } else {
          await createIndicadorObraUseCase.createIndicadorObra(
            selectedObra.idObra,
            form.idIndicadores,
          );
        }

        if (selectedObra.idObra) {
          await loadIndicadoresDropdown(selectedObra.idObra);
        }

        await loadIndicadoresObra();
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao salvar indicador!"),
        );
      } finally {
        setSaving(false);
        setIndicadorObraModal(false);
      }
    }
  }, [
    selectedObra,
    selectedIndicadorObra,
    createIndicadorObraUseCase,
    updateIndicadorObraUseCase,
    loadIndicadoresObra,
    loadIndicadoresDropdown,
    pushErrorNotification,
  ]);

  const handleDeleteIndicadorObraModalOpenState = useCallback(
    (idObraIndicador: number | null, openState: boolean) => {
      if (openState) {
        if (idObraIndicador && indicadorObraLista) {
          setSelectedIndicadorObra(
            indicadorObraLista.find(
              (x) => x.idObraIndicador === idObraIndicador,
            ),
          );
        } else {
          setSelectedIndicadorObra(undefined);
        }
      }
      setIndicadorObraDeleteModal(openState);
    },
    [indicadorObraLista],
  );

  const handleDeleteConfirmIndicadorObra = useCallback(async () => {
    if (selectedIndicadorObra) {
      try {
        setSaving(true);
        await deleteIndicadorObraUseCase.deleteIndicadorObra(
          selectedIndicadorObra.idObraIndicador,
        );

        if (selectedIndicadorObra.idObra) {
          await loadIndicadoresDropdown(selectedIndicadorObra.idObra);
        }

        await loadIndicadoresObra();
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao excluir indicador!"),
        );
      } finally {
        setSaving(false);
        setIndicadorObraDeleteModal(false);
      }
    }
  }, [
    selectedIndicadorObra,
    deleteIndicadorObraUseCase,
    loadIndicadoresDropdown,
    loadIndicadoresObra,
    pushErrorNotification,
  ]);

  const handleIndicadorObraPageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useObrasInformacoesGeraisPagination
          .getState()
          .indicadoresObraSetter.setPage(page + 1);
        await loadIndicadoresObra({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados dos indicadores!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [loadIndicadoresObra, pushErrorNotification],
  );

  const handleIndicadorObraRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        useObrasInformacoesGeraisPagination
          .getState()
          .indicadoresObraSetter.setPage(1);
        useObrasInformacoesGeraisPagination
          .getState()
          .indicadoresObraSetter.setRowsPerPage(rowsPerPage);
        await loadIndicadoresObra({ page: 1, rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados dos indicadores!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [loadIndicadoresObra, pushErrorNotification],
  );

  // #endregion Objetivos Específicos da Obra

  /**
   * Documento
   */

  // #region Documento

  const handleObraArquivoListaModalOpenState = useCallback(
    (openState: boolean) => {
      setObraArquivoListaModal(openState);
    },
    [],
  );

  const handleCadastroObraArquivoOpenState = useCallback(
    (idObraArquivo: number | null, openState: boolean) => {
      if (openState) {
        if (idObraArquivo && obraArquivoLista) {
          setSelectedObraArquivo(
            obraArquivoLista.find((x) => x.idObraArquivo === idObraArquivo),
          );
        } else {
          setSelectedObraArquivo(undefined);
        }
      }
      setObraArquivoModal(openState);
    },
    [obraArquivoLista],
  );

  const handleObraArquivoSubmit = useCallback(
    async (form: ObraArquivoForm) => {
      if (selectedObra) {
        try {
          setSaving(true);
          if (selectedObraArquivo) {
            await updateObraArquivoUseCase.updateObraArquivo(
              selectedObraArquivo.idObraArquivo,
              form.statusArquivo,
              form.nomeDocumento,
              form.file,
              form.dataVencimento,
            );
          } else {
            await createObraArquivoUseCase.createObraArquivo(
              selectedObra.idObra,
              form.statusArquivo,
              form.nomeDocumento,
              form.file,
              form.dataVencimento,
            );
          }
          await loadObraArquivos();
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao excluir documento!"),
          );
        } finally {
          setSaving(false);
          setObraArquivoModal(false);
        }
      }
    },
    [
      selectedObra,
      selectedObraArquivo,
      createObraArquivoUseCase,
      updateObraArquivoUseCase,
      loadObraArquivos,
      pushErrorNotification,
    ],
  );

  const handleDeleteObraArquivoModalOpenState = useCallback(
    (idObraArquivo: number | null, openState: boolean) => {
      if (openState) {
        if (idObraArquivo && obraArquivoLista) {
          setSelectedObraArquivo(
            obraArquivoLista.find((x) => x.idObraArquivo === idObraArquivo),
          );
        } else {
          setSelectedObraArquivo(undefined);
        }
      }
      setObraArquivoDeleteModal(openState);
    },
    [obraArquivoLista],
  );

  const handleDeleteConfirmObraArquivo = useCallback(async () => {
    if (selectedObraArquivo) {
      try {
        setSaving(true);
        await deleteObraArquivoUseCase.deleteObraArquivo(
          selectedObraArquivo.idObraArquivo,
        );
        await loadObraArquivos();
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao excluir documento!"),
        );
      } finally {
        setSaving(false);
        setObraArquivoDeleteModal(false);
      }
    }
  }, [
    selectedObraArquivo,
    deleteObraArquivoUseCase,
    loadObraArquivos,
    pushErrorNotification,
  ]);

  const handleObraArquivoPageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useObrasInformacoesGeraisPagination
          .getState()
          .obraArquivosSetter.setPage(page + 1);
        await loadObraArquivos({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados de arquivos de obra!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [loadObraArquivos, pushErrorNotification],
  );

  const handleObraArquivoRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        useObrasInformacoesGeraisPagination
          .getState()
          .obraArquivosSetter.setPage(1);
        useObrasInformacoesGeraisPagination
          .getState()
          .obraArquivosSetter.setRowsPerPage(rowsPerPage);
        await loadObraArquivos({ page: 1, rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados de arquivos de obra!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [loadObraArquivos, pushErrorNotification],
  );

  // #endregion Documento

  /**
   * Informações da Obra
   */

  // #region Informações da Obra

  const handleObraInformacoesListaOpenState = useCallback(
    (openState: boolean) => {
      setObraInformacoesListaModal(openState);
    },
    [],
  );

  const handleCadastroObraInformacoesOpenState = useCallback(
    (idObraInformacoes: number | null, openState: boolean) => {
      if (openState) {
        if (idObraInformacoes && obraInformacoesLista) {
          setSelectedObraInformacoes(
            obraInformacoesLista.find(
              (x) => x.idObraInformacoes === idObraInformacoes,
            ),
          );
        } else {
          setSelectedObraInformacoes(undefined);
        }
      }
      setObraInformacoesModal(openState);
    },
    [obraInformacoesLista],
  );

  const handleObraInformacoesSubmit = useCallback(
    async (form: ObraInformacoesForm) => {
      if (selectedObra) {
        try {
          setSaving(true);
          if (selectedObraInformacoes) {
            await updateObraInformacoesUseCase.updateObraInformacoes(
              selectedObraInformacoes.idObraInformacoes,
              form.idFuncionario,
              form.data,
              form.descInformacoes,
            );
          } else {
            await createObraInformacoesUseCase.createObraInformacoes(
              selectedObra.idObra,
              form.idFuncionario,
              form.data,
              form.descInformacoes,
            );
          }
          await loadObraInformacoes();
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao salvar informação!"),
          );
        } finally {
          setSaving(false);
          setObraInformacoesModal(false);
        }
      }
    },
    [
      selectedObra,
      selectedObraInformacoes,
      createObraInformacoesUseCase,
      updateObraInformacoesUseCase,
      pushErrorNotification,
      loadObraInformacoes,
    ],
  );

  const handleDeleteObraInformacoesModalOpenState = useCallback(
    (idObraInformacoes: number | null, openState: boolean) => {
      if (openState) {
        if (idObraInformacoes && obraInformacoesLista) {
          setSelectedObraInformacoes(
            obraInformacoesLista.find(
              (x) => x.idObraInformacoes === idObraInformacoes,
            ),
          );
        } else {
          setSelectedObraInformacoes(undefined);
        }
      }
      setObraInformacoesDeleteModal(openState);
    },
    [obraInformacoesLista],
  );

  const handleDeleteConfirmObraInformacoes = useCallback(async () => {
    if (selectedObraInformacoes) {
      try {
        setSaving(true);
        await deleteObraInformacoesUseCase.deleteObraInformacoes(
          selectedObraInformacoes.idObraInformacoes,
        );
        await loadObraInformacoes();
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao excluir informações!"),
        );
      } finally {
        setSaving(false);
        setObraInformacoesDeleteModal(false);
      }
    }
  }, [
    selectedObraInformacoes,
    deleteObraInformacoesUseCase,
    loadObraInformacoes,
    pushErrorNotification,
  ]);

  const handleObraInformacoesPageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useObrasInformacoesGeraisPagination
          .getState()
          .obraInformacoesSetter.setPage(page + 1);
        await loadObraInformacoes({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados de informações de obra!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [loadObraInformacoes, pushErrorNotification],
  );

  const handleObraInformacoesRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        useObrasInformacoesGeraisPagination
          .getState()
          .obraInformacoesSetter.setPage(1);
        useObrasInformacoesGeraisPagination
          .getState()
          .obraInformacoesSetter.setRowsPerPage(rowsPerPage);
        await loadObraInformacoes({ page: 1, rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao buscar dados de informações de obra!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [loadObraInformacoes, pushErrorNotification],
  );

  const handleFiltersDataChange = useDebouncedCallback(
    debounceHandlerRef,
    async (data: Date | null) => {
      useObrasInformacoesGeraisFilter.setState({ data });
      return async () => {
        try {
          setLoading(true);
          await loadObraInformacoes({ data });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao buscar dados de informações de obra!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [pushErrorNotification, loadObraInformacoes],
  );

  const handleFiltersInformacoesChange = useDebouncedCallback(
    debounceHandlerRef,
    async (informacoes: string | null) => {
      useObrasInformacoesGeraisFilter.setState({ informacoes });
      return async () => {
        try {
          setLoading(true);
          await loadObraInformacoes({ informacoes });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao buscar dados de informações de obra!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [pushErrorNotification, loadObraInformacoes],
  );

  const handleObraInformacoesClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      useObrasInformacoesGeraisFilter.setState({
        data: null,
        informacoes: null,
      });
      await loadObraInformacoes({ data: null, informacoes: null });
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao buscar dados de informações de obra!"),
      );
    } finally {
      setLoading(false);
    }
  }, [loadObraInformacoes, pushErrorNotification]);

  // #endregion Informações da Obra

  /**
   * History
   */

  // #region History

  const handleOpenChangeHistory = useCallback(
    (idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
      if (idTabela && tipoTabela) {
        changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
      } else {
        if (idObra) {
          changeHistory.handleOpenChangeHistory(
            idObra,
            LogTipoTabela.OBRA,
            true,
          );
        }
      }
    },
    [idObra, changeHistory],
  );

  // #endregion History

  return {
    loading,
    saving,
    errorMessage,
    hookForm,
    idObra,

    estOrgLista: estOrgLista ?? [],
    atividadeObraLista: atividadeObraLista ?? [],
    especificidadeObraLista: especificidadeObraLista ?? [],
    indicadorObraLista: indicadorObraLista ?? [],
    obraArquivoLista: obraArquivoLista ?? [],
    obraInformacoesLista: obraInformacoesLista ?? [],

    selectedObra: selectedObra ?? null,
    selectedEstOrg,
    selectedAtividadeObra,
    selectedEspecificidadeObra,
    selectedIndicadorObra: selectedIndicadorObra ?? null,
    selectedObraArquivo,
    selectedObraInformacoes,

    obraDeleteModal,

    estOrgListaModal,
    estOrgModal,
    estOrgDeleteModal,

    atividadeListaObraModal,
    atividadeObraModal,
    atividadeObraDeleteModal,

    especificidadeListaObraModal,
    especificidadeObraModal,
    especificidadeObraDeleteModal,

    indicadorObraListaModal,
    indicadorObraModal,
    indicadorObraDeleteModal,

    obraArquivoListaModal,
    obraArquivoModal,
    obraArquivoDeleteModal,

    obraInformacoesListaModal,
    obraInformacoesModal,
    obraInformacoesDeleteModal,

    estOrgFileUpload,
    estOrgFileUploadUrl,
    cantFileUpload,
    cantFileUploadUrl,

    filtroMapaConcretagem,

    hookFormObraArquivo,

    changeHistory,

    obrasTopHeaderState,
    obrasReportState,

    filters,
    pagination,

    centroCustosDropdown: centroCustosDropdown ?? [],
    empreendimentosDropdown: empreendimentosDropdown ?? [],
    estOrgfuncionariosDropdown: filteredEstOrgfuncionariosDropdown ?? [],
    responsavelDropdown: responsavelDropdown ?? [],
    corresponsavelDropdown: corresponsavelDropdown ?? [],
    indicadoresDropdown: indicadoresDropdown ?? [],
    fornecedoresDropDown: fornecedoresDropDown ?? [],

    funcionariosInfObraDropDown: funcionariosInfObraDropDown ?? [],

    funcionariosRevisadoDropDown: funcionariosRevisadoDropDown ?? [],
    funcionariosAprovadoDropDown: funcionariosAprovadoDropDown ?? [],

    handleSubmit: hookForm.handleSubmit(handleSubmit),
    handleDeleteObraModalOpenState,
    handleDeleteConfirmObra,
    handlePopperOptionSelect,
    handleReload,
    handleBack,
    handleStartEstOrgFileUpload,
    handleStartCantFileUpload,
    handleDeleteEstOrgFileUpload,
    handleDeleteCantFileUpload,
    handleViewObraArquivo,
    handleDownloadObraArquivo,

    handleEstOrgListaOpenState,
    handleCadastroEstOrgOpenState,
    handleEstOrgSubmit,
    handleDeleteEstOrgModalOpenState,
    handleDeleteConfirmEstOrg,
    handleEstOrgPageChange,
    handleEstOrgRowsPerPageChange,

    handleAtividadeListaModalOpenState,
    handleCadastroAtividadeOpenState,
    handleAtividadeSubmit,
    handleDeleteAtividadeModalOpenState,
    handleDeleteConfirmAtividade,
    handleAtividadesPageChange,
    handleAtividadesRowsPerPageChange,

    handleEspecificidadeListaObraModalOpenState,
    handleCadastroEspecificidadeObraOpenState,
    handleEspecificidadeObraSubmit,
    handleDeleteEspecificidadeObraModalOpenState,
    handleDeleteConfirmEspecificidadeObra,
    handleEspecificidadesObraPageChange,
    handleEspecificidadesObraRowsPerPageChange,

    handleIndicadorObraListaModalOpenState,
    handleCadastroIndicadorObraOpenState,
    handleIndicadorObraSubmit,
    handleDeleteIndicadorObraModalOpenState,
    handleDeleteConfirmIndicadorObra,
    handleIndicadorObraPageChange,
    handleIndicadorObraRowsPerPageChange,

    handleObraArquivoListaModalOpenState,
    handleCadastroObraArquivoOpenState,
    handleObraArquivoSubmit,
    handleDeleteObraArquivoModalOpenState,
    handleDeleteConfirmObraArquivo,
    handleObraArquivoPageChange,
    handleObraArquivoRowsPerPageChange,

    handleObraInformacoesListaOpenState,
    handleCadastroObraInformacoesOpenState,
    handleObraInformacoesSubmit,
    handleDeleteObraInformacoesModalOpenState,
    handleDeleteConfirmObraInformacoes,
    handleObraInformacoesPageChange,
    handleObraInformacoesRowsPerPageChange,
    handleFiltersDataChange,
    handleFiltersInformacoesChange,
    handleObraInformacoesClearFilters,

    handleOpenChangeHistory,
  };
};
